/**
 * Web app-shell coordinator for subscription moments that are not tied to a
 * screen (mounted once in `app/(app)/_layout.web.tsx`):
 *
 * - J "You're on Free now." once per expiry episode (`useLapseNotice`); every
 *   dismissal acknowledges the episode. Hidden while a purchase/sync is in
 *   flight and when the end date is unknown.
 * - Provider handoff return: web management pages leave the app, so when a
 *   parked handoff (`lib/subscription-handoff.web.ts`) is found the cancel
 *   journey re-opens on H in its "Let's check your plan." state and syncs.
 *   Only a synced `willRenew === false` then shows "Renewal is off."
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useLapseNotice } from '../../hooks/useLapseNotice';
import { useUpgradeModal } from '../../providers/useUpgradeModal';
import { buildPublicProfileUrl } from '../../lib/public-web';
import { withCvShareRef } from '@tabbio/utils/analytics/cv-traffic';
import { trackProductEvent } from '../../lib/product-analytics';
import { formatCancelAccessDate } from '../../lib/subscription-journey/cancel-journey';
import {
  readProviderHandoff,
  type ProviderHandoff,
} from '../../lib/subscription-handoff.web';
import { CancelPlanDialog } from '../settings/CancelPlanDialog.web';
import { NovaMood } from './NovaMood.web';
import {
  InfoDialog,
  JourneyDialog,
  SubButton,
  SubGroup,
  SubHeading,
  SubRow,
} from './journey-ui.web';

const TIER_NAMES = { plus: 'Plus', pro: 'Pro' } as const;

export function SubscriptionLapseCoordinator() {
  const { user } = useAuth();
  const [handoff, setHandoff] = useState<ProviderHandoff | null>(null);

  useEffect(() => {
    setHandoff(readProviderHandoff(user?.id));
  }, [user?.id]);

  if (handoff) {
    return (
      <CancelPlanDialog
        open
        entry={{ step: 'provider', intent: handoff.intent, resume: true }}
        onClose={() => setHandoff(null)}
      />
    );
  }
  return <LapseNotice />;
}

function LapseNotice() {
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth();
  const { episode, acknowledge } = useLapseNotice();
  const upgradeModal = useUpgradeModal();
  const [info, setInfo] = useState<'cv' | 'profile' | null>(null);

  useEffect(() => {
    if (episode) trackProductEvent('subscription_lapse_notice_viewed', { tier: episode.tier, surface: 'web' });
  }, [episode]);

  if (!episode) return null;
  const date = formatCancelAccessDate(episode.endedAt, i18n.resolvedLanguage ?? i18n.language);
  if (!date) return null;
  const profileUrl = user?.username
    ? buildPublicProfileUrl(user.username, 'individual')
    : null;

  const dismiss = (via: 'close' | 'free' | 'explore') => {
    trackProductEvent('subscription_lapse_notice_dismissed', { via, surface: 'web' });
    acknowledge();
  };

  return (
    <>
      <JourneyDialog
        title={t('subscription.lapsed.title')}
        onClose={() => dismiss('close')}
        zIndex={1050}
        footer={
          <>
            <SubButton
              onClick={() => {
                dismiss('explore');
                upgradeModal.open('individual');
              }}
            >
              {t('subscription.lapsed.explorePlans')}
            </SubButton>
            <SubButton kind="secondary" onClick={() => dismiss('free')}>
              {t('subscription.lapsed.continueFree')}
            </SubButton>
          </>
        }
      >
        <NovaMood mood="farewell" size={96} />
        <div style={{ marginTop: 12 }}>
          <SubHeading
            title={t('subscription.lapsed.heading')}
            subtitle={t('subscription.lapsed.subtitle')}
          />
        </div>
        <div className="tabbio-sub-plain">
          <strong>
            {t('subscription.lapsed.ended', { tier: TIER_NAMES[episode.tier], date })}
          </strong>
          <p>{t('subscription.lapsed.novaNote')}</p>
        </div>
        <div style={{ marginTop: 16 }}>
          <SubGroup>
            <SubRow
              icon="description"
              title={t('subscription.lapsed.savedCv')}
              sub={t('subscription.lapsed.stillYours')}
              onClick={() => setInfo('cv')}
            />
            <SubRow
              icon="link"
              title={t('subscription.lapsed.publicProfile')}
              sub={t('subscription.lapsed.readyToShare')}
              onClick={() => setInfo('profile')}
            />
          </SubGroup>
        </div>
      </JourneyDialog>
      {info === 'cv' ? (
        <InfoDialog
          title={t('subscription.info.cv.title')}
          body={t('subscription.info.cv.body')}
          onClose={() => setInfo(null)}
        />
      ) : null}
      {info === 'profile' ? (
        <InfoDialog
          title={t('subscription.info.profile.title')}
          body={t('subscription.info.profile.body')}
          onClose={() => setInfo(null)}
        >
          {profileUrl ? (
            <div className="tabbio-sub-plain" dir="ltr" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', fontSize: 14 }}>
                {profileUrl.replace(/^https?:\/\//, '')}
              </span>
              <CopyButton value={withCvShareRef(profileUrl, 'copy')} />
            </div>
          ) : null}
        </InfoDialog>
      ) : null}
    </>
  );
}

function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="tabbio-sub-link"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true));
      }}
    >
      {copied ? t('subscription.info.profile.copied') : t('subscription.info.profile.copy')}
    </button>
  );
}
